import { LitElement, html, css } from 'https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js'

/**
 * Skio Universal Plan Picker V2 - Homepage Featured Product Selector Integration
 * @docs https://integrate.skio.com/
 *
 * Extends the original Skio Plan Picker with custom styling for the homepage featured product selector
 */

export class SkioPlanPickerV2 extends LitElement {
  static properties = {
    key: { type: String },
    product: { type: Object },
    productHandle: { type: String },
    selectedVariant: { type: Object },
    options: { type: Object },
    moneyFormat: { type: String },
    quantity: { type: Number },
    rules: { type: Array },
    rule: { type: Object },
    cart: { type: Object },
    availableSellingPlanGroups: { state: true },
    selectedSellingPlanGroup: { type: Object },
    selectedSellingPlan: { type: Object },
    disableUrl: { type: Boolean },
    debug: { type: Boolean },
  }

  static styles = [
    css`
      :host {
        display: block;
        width: 100%;
      }

      .skio-plan-picker-v2 {
        display: block;
        width: 100%;
        margin: 0;
        padding: 0;
        border: 0;
        font-family: inherit;
      }

      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        border: 0;
      }

      /* Purchase option card */
      .subscription-option {
        position: relative;
        display: block;
        margin-bottom: 20px;
        padding: 1rem;
        background: #fff;
        border: 1px solid #333;
        border-radius: 0;
        cursor: pointer;
        transition: border-color 0.2s ease, background-color 0.2s ease;
        box-sizing: border-box;
      }

      .subscription-option.selected {
        background: #f8f9fa;
        border: 2px solid #dc2626;
      }

      .subscription-option input[type='radio'] {
        position: absolute;
        width: 0;
        height: 0;
        opacity: 0;
      }

      /* Top row */
      .option-content {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        align-items: start;
        gap: 16px;
        width: 100%;
      }

      .option-left {
        min-width: 0;
      }

      .option-title-row {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        margin: 0;
      }

      .option-title {
        margin: 0;
        color: #333;
        font-size: 1.1rem;
        font-weight: 700;
        line-height: 1;
      }

      .option-subtitle {
        margin-top: 5px;
        color: #666;
        font-size: clamp(12px, 2.5cqw, 16px);
        font-weight: 400;
        line-height: 1.2;
      }

      .price-per-stick {
        margin-top: 4px;
        color: #666;
        font-size: clamp(12px, 2.5cqw, 16px);
        font-weight: 400;
        line-height: 1;
      }

      /* Price */
      .option-price,
      .option-price-line {
        display: flex;
        align-items: baseline;
        justify-content: flex-end;
        gap: 8px;
        margin: 0;
        line-height: 1;
        white-space: nowrap;
      }

      .option-price {
        justify-self: end;
      }

      .price-current,
      .price-compare {
        line-height: 1;
      }

      .price-current {
        color: #333;
        font-size: 1.1rem;
        font-weight: 700;
      }

      .price-compare {
        color: #6b7280;
        font-size: 0.9rem;
        text-decoration: line-through;
      }

      .price-sale {
        color: #dc2626;
      }

      .sale-label {
        color: #dc2626;
        font-size: 0.8rem;
        font-style: italic;
        font-weight: 700;
        line-height: 1;
        text-transform: uppercase;
        white-space: nowrap;
      }

      /* Discount pill */
      .discount-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 5px 8px;
        background: #dc2626;
        color: #fff;
        border-radius: 9999px;
        box-shadow: none;
        font-size: 12px;
        font-weight: 700;
        line-height: 1;
        white-space: nowrap;
      }

      .discount-badge--mobile {
        display: none;
      }

      /* Delivery frequency */
      .frequency-selector {
        margin-top: 0.75rem;
      }

      .frequency-label {
        display: block;
        margin-bottom: 0.25rem;
        color: #666;
        font-size: 0.875rem;
        font-weight: 500;
      }

      .frequency-dropdown {
        width: 100%;
        padding: 0.5rem 2rem 0.5rem 0.75rem;
        background:
          #f7f7f7
          url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")
          no-repeat right 0.75rem center / 1rem;
        border: 1px solid #ccc;
        border-radius: 0;
        font-size: 0.8rem;
        appearance: none;
      }

      /* Benefits */
      .subscription-benefits {
        display: flex;
        flex-direction: column;
        gap: 0;
        margin-top: 1rem;
      }

      .benefit-item {
        display: flex;
        align-items: center;
        gap: 0.6rem;
        margin-bottom: 5px;
        line-height: 1;
      }

      .benefit-check {
        color: #16a34a;
        font-size: 1.125rem;
        font-weight: 700;
        line-height: 1;
      }

      .benefit-text {
        color: #333;
        font-size: 14px;
        font-weight: 500;
        line-height: 1;
      }

      /* One-time warning */
      .onetime-warning {
        display: none;
        margin-top: 1rem;
      }

      .onetime-warning.show {
        display: block;
      }

      .warning-item {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }

      .warning-icon {
        color: #dc2626;
        font-size: 1.125rem;
        font-weight: 700;
      }

      /* Setup / debug mode */
      .setupMode {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 1rem;
        padding: 1rem;
        color: #000;
        border-style: solid;
        border-width: 4px;
        border-image: repeating-linear-gradient(
          -55deg,
          #000,
          #000 20px,
          #ffb101 20px,
          #ffb101 40px
        ) 10;
        font-size: 14px;
      }

      .setupMode::after {
        content: 'Setup Mode V2';
        position: absolute;
        top: 0;
        right: 0;
        padding: 0.25rem 1rem;
        background: #000;
        color: #fff;
        font-size: 13px;
      }

      /* Mobile */
      @media (max-width: 749px) {
        .option-content {
          gap: 12px;
        }

        .option-title-row {
          gap: 6px;
        }

        .option-title {
          line-height: 0.98;
        }

        .discount-badge--desktop {
          display: none;
        }

        .subscription-price {
          flex-direction: column;
          align-items: flex-end;
          justify-content: flex-start;
          align-self: start;
          gap: 4px;
        }


        .discount-badge--mobile {
          display: inline-flex;
          margin-top: 2px;
        }

        .subscription-benefits {
          margin-top: 0;
          margin-bottom: 5px;
          transform: translateY(15px);
        }
      }
    `,
  ]

  constructor() {
    super()

    // Debug Variables
    this.debug = this.debug || window?.Shopify?.designMode
    this.variantChanged = false

    this.rules = []
    this.rule = null
    this.quantity = 1
    this.showDetailsHover = false

    this.saleStartDate = new Date('2026-07-02T00:00:00')
    this.saleEndDate = new Date('2026-07-05T00:00:00')
    this.saleDiscountPercent = 20
  }

  async connectedCallback() {
    super.connectedCallback()

    this.log('Mounted V2')

    if (this.productHandle) {
      this.loading = true
      this.fetchProduct(this.productHandle)
    }

    this.bindFormEvents()
    this.bindCartEvents()

    this.rules = await this.fetchRules()
    await this.getRule()
  }

  render() {
    if (!this.viable() || this.options?.layout === 'hidden') {
      return this.debug ? this.setupMode() : ''
    }

    if (this.selectedVariant.selling_plan_allocations.length === 0 && !this.options?.show_without_subscription) {
      return this.debug ? this.setupMode() : ''
    }

    if (this.availableSellingPlanGroups?.length == 0 && !this.options?.show_without_subscription) return

    return html`
      ${this.debug ? this.setupMode() : null}

      <div class="skio-plan-picker-v2">
        ${this.renderSubscriptionOptions()}
        ${this.renderOnetimeOption()}
      </div>
    `
  }

  renderSubscriptionOptions() {
    if (!this.availableSellingPlanGroups?.length) return ''

    return this.availableSellingPlanGroups.map(group => html`
      <label
        class="subscription-option ${this.selectedSellingPlanGroup == group ? 'selected' : ''}"
        @click=${() => this.selectSellingPlanGroup(group)}
      >
        <input
          type="radio"
          name="purchase_option_skio"
          value="subscription"
          ?checked=${this.selectedSellingPlanGroup == group}
          @change=${() => this.selectSellingPlanGroup(group)}
        />

        <div class="option-content">
          <div class="option-left">

            <div class="option-title-row">
              <h4 class="option-title">
                ${group.name !== 'Prepaid' ? 'Autoship & Save' : group.name}
              </h4>

              ${this.renderDiscountBadge(group.selected_selling_plan, 'discount-badge--desktop')}
            </div>

            ${this.getAvailableSellingPlans(group).length === 1 ? html`
              <div class="option-subtitle">
                ${this.getStickCount()} stick packs ${this.deliveryText(group.selected_selling_plan)}
              </div>
            ` : ''}

            <div class="price-per-stick">
              ${this.subscriptionPricePerStick(group)} / Day
            </div>

            ${this.getAvailableSellingPlans(group).length > 1
              ? html`
                  <div class="frequency-selector">
                    <label class="frequency-label">Delivery frequency</label>

                    <select
                      class="frequency-dropdown"
                      @change=${event => this.selectSellingPlan(event.target.value)}
                    >
                      ${this.getAvailableSellingPlans(group).map(
                        plan => html`
                          <option
                            value="${plan.id}"
                            ?selected=${group.selected_selling_plan.id === plan.id}
                          >
                            ${plan.name}
                          </option>
                        `
                      )}
                    </select>
                  </div>
                `
              : ''}

          </div>

          <div class="option-price subscription-price">
            ${this.renderSubscriptionPrice(group)}
            ${this.renderDiscountBadge(group.selected_selling_plan, 'discount-badge--mobile')}
          </div>
        </div>

        ${this.selectedSellingPlanGroup == group
          ? html`
              <div class="subscription-benefits">
                <div class="benefit-item">
                  <span class="benefit-check">✓</span>
                  <span class="benefit-text">
                    ${this.options?.benefit_1 || 'FREE express shipping'}
                  </span>
                </div>

                <div class="benefit-item">
                  <span class="benefit-check">✓</span>
                  <span class="benefit-text">
                    ${this.options?.benefit_2 || 'Pause or cancel anytime'}
                  </span>
                </div>

                <div class="benefit-item">
                  <span class="benefit-check">✓</span>
                  <span class="benefit-text">
                    ${this.options?.benefit_3 || '30-day money back guarantee'}
                  </span>
                </div>
              </div>
            `
          : ''}
      </label>
    `)
  }

  renderSubscriptionPrice(group) {
    return html`
      <div class="option-price-line">
        ${this.isSaleActive()
          ? html`
              <span class="price-compare">
                ${this.money(this.selectedVariant.price)}
              </span>

              <span class="price-current price-sale">
                ${this.money(
                  this.salePriceFromCents(
                    this.price(group.selected_selling_plan, false)
                  )
                )}
              </span>

              <span class="sale-label">
                ${this.saleLabel()}
              </span>
            `
          : html`
              <span class="price-current">
                ${this.price(group.selected_selling_plan)}
              </span>

              ${this.shouldShowSubscriptionComparePrice(group.selected_selling_plan)
                ? html`
                    <span class="price-compare">
                      ${this.money(
                        this.selectedVariant.compare_at_price ||
                        this.selectedVariant.price
                      )}
                    </span>
                  `
                : ''}
            `}
      </div>
    `
  }

  getStickCount() {
    const text = [
      this.product?.title,
      this.productHandle,
      this.selectedVariant?.title,
      this.selectedVariant?.name
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

    if (text.includes('duo')) return 64

    const match = text.match(/\b(16|32|64)\s*(ct|count|stick|sticks)\b/)
    if (match) return Number(match[1])

    return 32
  }

  deliveryText(sellingPlan) {
    if (!sellingPlan) return ''

    const policy = sellingPlan.delivery_policy

    if (policy?.interval && policy?.interval_count) {
      const count = Number(policy.interval_count)
      const interval = String(policy.interval).toLowerCase()

      if (count === 1) {
        return `every ${interval}`
      }

      return `every ${count} ${interval}s`
    }

    const name = sellingPlan.name || ''

    const match = name.match(/(\d+)\s*(day|week|month|year)s?/i)

    if (match) {
      const count = Number(match[1])
      const interval = match[2].toLowerCase()

      return count === 1
        ? `every ${interval}`
        : `every ${count} ${interval}s`
    }

    return ''
  }

  subscriptionPricePerStick(group) {
    let cents = this.price(group.selected_selling_plan, false)

    if (this.isSaleActive()) {
      cents = this.salePriceFromCents(cents)
    }

    return this.money(Math.round(cents / this.getStickCount()))
  }

  onetimePricePerStick() {
    let cents = this.selectedVariant.price

    if (this.isSaleActive()) {
      cents = this.salePriceFromCents(cents)
    }

    return this.money(Math.round(cents / this.getStickCount()))
  }

  renderDiscountBadge(sellingPlan, modifierClass = '') {
    const discountText = this.discountText(sellingPlan)
    if (!discountText) return ''

    return html`
      <div class="discount-badge ${modifierClass}">
        ${this.isSaleActive() ? 'BEST VALUE' : discountText}
      </div>
    `
  }

  shouldShowSubscriptionComparePrice(sellingPlan) {
    return (
      this.selectedVariant?.compare_at_price > this.selectedVariant.price ||
      this.selectedVariant.price > this.price(sellingPlan, false)
    )
  }

  renderOnetimeOption() {
    if (this.product.requires_selling_plan) return ''

    return html`
      <label
        class="subscription-option ${!this.selectedSellingPlanGroup ? 'selected' : ''}"
        @click=${() => this.selectSellingPlanGroup(null)}
      >
        <input
          type="radio"
          name="purchase_option_skio"
          value="onetime"
          ?checked=${!this.selectedSellingPlanGroup}
          @change=${() => this.selectSellingPlanGroup(null)}
        />

        <div class="option-content">
          <div class="option-left">
            <h4 class="option-title">One-Time Purchase</h4>

            <div class="option-subtitle">
              ${this.getStickCount()} stick packs
            </div>

            <div class="price-per-stick">
              ${this.onetimePricePerStick()} / Day
            </div>
          </div>

          <div class="option-price">
            ${this.isSaleActive()
              ? html`
                  <span class="price-compare">
                    ${this.money(this.selectedVariant.price)}
                  </span>

                  <span class="price-current">
                    ${this.money(
                      this.salePriceFromCents(this.selectedVariant.price)
                    )}
                  </span>
                `
              : html`
                  <span class="price-current">
                    ${this.money(this.selectedVariant.price)}
                  </span>

                  ${this.selectedVariant?.compare_at_price >
                  this.selectedVariant.price
                    ? html`
                        <span class="price-compare">
                          ${this.money(this.selectedVariant.compare_at_price)}
                        </span>
                      `
                    : ''}
                `}
          </div>
        </div>

        ${!this.selectedSellingPlanGroup
          ? html`
              <div class="onetime-warning show">
                <div class="warning-item">
                  <span class="warning-icon">❌</span>
                  <span class="benefit-text">
                    ${this.options?.negative || 'No FREE shipping'}
                  </span>
                </div>
              </div>
            `
          : ''}
      </label>
    `
  }

  setupMode() {
    return html`
      <div class="setupMode">
        Skio Plan Picker V2 - Homepage Integration
        <small>This will not appear on the live site.</small>
        <small>Key: ${this.key}</small>
        <div>Product: ${this.product ? '✓' : '❌'}</div>
        <div>Selected Variant: ${this.selectedVariant ? '✓' : '❌'}</div>
        <div>Form: ${this.form ? '✓' : '❌'}</div>
        <div>Variant Input: ${this.variantInput ? '✓' : '❌'}</div>
      </div>
    `
  }

  // Copy all the utility methods from the original component
  async fetchRules() {
    if (sessionStorage.getItem('skio-discount-rules')) {
      return JSON.parse(sessionStorage.getItem('skio-discount-rules'))
    } else {
      const rules = await fetch(`https://api.skio.com/storefront-http/get-rules-by-domain-or-hostname?domain=${window?.Shopify?.shop}`)
        .then(response => response.json())
        .then(response => {
          return response.rules.filter(rule => rule.type !== 'surpriseDelight').sort((a, b) => a.minQuantityToDiscount - b.minQuantityToDiscount)
        })

      sessionStorage.setItem('skio-discount-rules', JSON.stringify(rules))
      return rules
    }
  }

  eligibleItemCount(rule) {
    const eligibleQuantity = rule?.productVariantIds.some(gid => gid.includes(this.selectedVariant.id)) ? this.quantity : 0

    if (!this.cart?.items) return eligibleQuantity

    const cartItems = Number(
      this.cart?.items.reduce(
        (aggregate, item) =>
          rule?.productVariantIds.some(gid => gid.includes(item.id) && item.selling_plan_allocation && gid.includes(this.selectedVariant.id)) ? aggregate + item.quantity : aggregate,
        0
      )
    )

    return cartItems + eligibleQuantity
  }

  getRule() {
    let largestMinQuantity = {
      value: -1,
      index: -1,
    }

    this.rules.forEach((rule, index) => {
      if (rule.code && largestMinQuantity.value < rule.minQuantityToDiscount) {
        if (rule.minQuantityToDiscount <= this.eligibleItemCount(rule)) {
          largestMinQuantity.value = rule.minQuantityToDiscount
          largestMinQuantity.index = index
        }
      }
    })

    this.rule = largestMinQuantity.index > -1 ? this.rules[largestMinQuantity.index] : null
  }

  bindCartEvents() {
    document.addEventListener('CartUpdated', event => {
      this.cart = event.detail
    })
  }

  bindFormEvents() {
    // Find the form - look for the featured product selector form
    this.form = document.querySelector('[data-product-form-element]') ||
               document.querySelector(`#${this.options?.form_id}`) ||
               this.findProductForm()

    if (!this.form) {
      this.error('No valid product form found')
      return
    }

    // Add debug styling if debug mode is enabled
    if (this.debug) {
      this.form.style.borderStyle = 'solid'
      this.form.style.borderWidth = '4px'
      this.form.style.borderImage = 'repeating-linear-gradient(-55deg,#000,#000 20px,#ffb101 20px,#ffb101 40px)10'
    }

    // Ensure form has an ID
    if (!this.form.getAttribute('id')) {
      this.form.setAttribute('id', `skio-form-${this.product.id}`)
    }

    // Set form attribute on selling plan input
    const sellingPlanInput = this.querySelector('input[name="selling_plan"]')
    if (sellingPlanInput) {
      sellingPlanInput.setAttribute('form', this.form.getAttribute('id'))
    }

    this.variantInput = this.form.querySelector('[name="id"]')
    if (!this.variantInput) this.variantInput = document.querySelector(`[name="id"][form="${this.options?.form_id}"]`)

    if (this.variantInput) {
      this.variantInput.addEventListener('change', e => {
        this.selectedVariant = this.product.variants.find(variant => variant.id == e.target.value)
        this.variantChanged = true
      })
    }

    const $quantityInput = this.form.querySelector('[name="quantity"]')
    if ($quantityInput) {
      this.quantity = Number($quantityInput.value)
      $quantityInput.addEventListener('input', () => {
        this.quantity = Number($quantityInput.value)
      })
    }

    // Listen for variant changes from the main form
    document.addEventListener('variantChanged', event => {
      const variantId = event.detail.variantId
      const variant = this.product.variants.find(item => item.id == variantId)

      if (variant) {
        this.selectedVariant = variant
        this.variantChanged = true
        this.requestUpdate()
      } else {
        this.log('Unable to find variant with id: ', variantId)
      }
    })
  }

  findProductForm() {
    const formFromParent = this.findFormThroughParents()
    if (formFromParent) return formFromParent

    const allProductForms = this.getAllProductForms()
    return allProductForms[0] || null
  }

  findFormThroughParents() {
    let currentElement = this
    let depth = 0
    const maxDepth = 5

    while (currentElement.parentElement && depth < maxDepth) {
      const forms = Array.from(currentElement.querySelectorAll('form'))
      const validForm = forms.find(form => this.isValidProductForm(form))

      if (validForm) return validForm

      currentElement = currentElement.parentElement
      depth++
    }

    return null
  }

  getAllProductForms() {
    return Array.from(document.forms).filter(form => {
      const hasVariantId = this.getVariantId(form)
      return this.isValidProductForm(form) && hasVariantId
    })
  }

  isValidProductForm(form) {
    const hasCartAction = form.getAttribute('action')?.includes('/cart/add')
    const hasSubmitButton = !!form.querySelector('button, [type="submit"]')
    return hasCartAction && hasSubmitButton
  }

  getVariantId(form) {
    const formData = new FormData(form)
    const idFromFormData = formData.get('id')
    if (idFromFormData) return Number(idFromFormData)

    const idInput = form.querySelector('[name="id"]')
    return idInput ? Number(idInput.value) : null
  }

  updated(changed) {
    if (!this.viable()) return

    if (changed.has('product') && this.product) {
      this.key = this.key ? this.key : this.product.id
    }

    if (changed.has('selectedVariant') && this.selectedVariant) {
      const filteredSellingPlanGroups = this.product.selling_plan_groups.filter(
        selling_plan_group =>
          selling_plan_group.app_id === 'SKIO' && !selling_plan_group.name.toLowerCase().includes('hidden-') && !selling_plan_group.name.toLowerCase().includes('dynamic box subscription')
      )

      this.availableSellingPlanGroups = filteredSellingPlanGroups.filter(selling_plan_group =>
        selling_plan_group.selling_plans.some(selling_plan =>
          this.selectedVariant.selling_plan_allocations.some(selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id)
        )
      )

      if (this.availableSellingPlanGroups?.length) {
        const url = new URL(window.location.href)
        const urlSelectedSellingPlanId = url.searchParams.get('selling_plan')

        this.availableSellingPlanGroups.forEach(group => {
          const availableSellingPlans = this.getAvailableSellingPlans(group)
          const urlSelectedSellingPlan = availableSellingPlans.find(plan => plan.id == urlSelectedSellingPlanId)
          const nameSelectedSellingPlan = availableSellingPlans.find(plan => plan.name === this.selectedSellingPlan?.name)
          const defaultSellingPlan = availableSellingPlans[0]

          group.selected_selling_plan = urlSelectedSellingPlan || nameSelectedSellingPlan || defaultSellingPlan
          group.selected = !!urlSelectedSellingPlan || !!nameSelectedSellingPlan
        })

        if ((!this.variantChanged && this.options?.start_onetime == false) || this.product.requires_selling_plan == true || urlSelectedSellingPlanId || this.selectedSellingPlan) {
          let selectedSellingPlanGroup = this.availableSellingPlanGroups.find(group => group.selected) || this.availableSellingPlanGroups[0]
          let selectedSellingPlanId = selectedSellingPlanGroup.selected_selling_plan.id
          this.selectSellingPlan(selectedSellingPlanId)
        } else {
          this.selectSellingPlanGroup(null)
        }
      } else {
        this.selectSellingPlanGroup(null)
      }
    }

    if (changed.has('selectedSellingPlan')) {
      const event = new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true,
        composed: true,
        detail: {
          variant: this.selectedVariant,
          sellingPlan: this.selectedSellingPlan,
          key: this.key,
        },
      })

      this.dispatchEvent(event)

      this.dispatchEvent(
        new CustomEvent('change', {
          detail: { value: this.selectedSellingPlan?.id },
        })
      )
    }

    this.updateForm()
    this.updateExternalPrice()

    if (!this.disableUrl) {
      this.updateURLParams()
    }

    if (changed.has('quantity')) {
      this.getRule()
    }

    if (changed.has('cart')) {
      this.getRule()
    }

    this.updateExternalElements()
  }

  updateExternalElements() {
    document.querySelectorAll(`[skio-price][skio-key="${this.key}"]`).forEach(el => {
      el.innerHTML = this.price(this.selectedSellingPlan)
    })

    document.querySelectorAll(`[skio-subscription-content]`).forEach(el => {
      el.style.display = this.selectedSellingPlan ? 'block' : 'none'
    })
  }

  log(...args) {
    args.unshift(`%c[skio plan picker v2][${this.key}]`, 'color: #8770f2;')
    console.log.apply(console, args)
  }

  error(...args) {
    args.unshift(`%c [skio plan picker v2][${this.key}]`, 'color: #ff0000')
    console.error.apply(console, args)
  }

  viable() {
    const errors = []

    const sellingPlanInput = this.querySelector('input[name=selling_plan]')
    if (!sellingPlanInput) {
      errors.push('Missing selling plan input')
    }

    if (!this.product) {
      errors.push('No product found. Please pass a product or productHandle to the skio-plan-picker-v2 component.')
    }
    if (!this.selectedVariant) {
      errors.push('No variant found. Please pass a product or productHandle to the skio-plan-picker-v2 component.')
    }
    if (!this.form) {
      errors.push('No form found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id".')
    }
    if (!this.variantInput) {
      errors.push('No variant input found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id"')
    }

    if (errors.length == 0) return true

    this.error('Errors', errors)
    return false
  }

  getAvailableSellingPlans(group) {
    let availableSellingPlans = group.selling_plans.filter(selling_plan =>
      this.selectedVariant.selling_plan_allocations.some(selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id)
    )

    return availableSellingPlans
  }

  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group
    this.selectedSellingPlan = group?.selected_selling_plan
  }

  selectSellingPlan(selling_plan_id) {
    const selectedGroup = this.availableSellingPlanGroups.find(group => group.selling_plans.some(plan => plan.id == selling_plan_id))
    const selectedSellingPlan = selectedGroup.selling_plans.find(plan => plan.id == selling_plan_id)

    selectedGroup.selected_selling_plan = selectedSellingPlan
    this.selectedSellingPlanGroup = selectedGroup
    this.selectedSellingPlan = selectedSellingPlan
  }

  updateExternalPrice() {
    document.querySelectorAll(this.options?.external_price_selector).forEach(el => {
      this.selectedSellingPlan ? (el.innerHTML = this.price(this.selectedSellingPlan)) : (el.innerHTML = this.money(this.selectedVariant.price))
    })
  }

  discountText(selling_plan) {
    const discount = this.discount(selling_plan)
    const hasInvalidDiscount = Object.values(discount).some(value => value === 0 || value === Infinity || value.toString().includes('-'))

    if (hasInvalidDiscount) {
      return ''
    } else {
      return `${discount.percent}% off`
    }
  }

  discount(selling_plan) {
    if (!selling_plan) return { percent: 0, amount: 0 }
    return this.getDiscountFromPriceAdjustment(selling_plan, selling_plan.price_adjustments[0])
  }

  getDiscountFromPriceAdjustment(selling_plan, price_adjustment) {
    const discount = {
      percent: 0,
      amount: 0,
      absolute: 0,
    }

    let multiplier = 1
    let sellingPlanGroup = this.product.selling_plan_groups.find(group => group.selling_plans.some(plan => plan.id == selling_plan.id))

    if (sellingPlanGroup?.name === 'Prepaid') {
      const str = selling_plan.name
      const intervalDate = /\b\d+\s*(days|weeks|months|years)\b/gi
      const intervalDateMatches = str.match(intervalDate)

      if (intervalDateMatches?.length) {
        const intervals = intervalDateMatches[0].match(/\d+/g)
        multiplier = intervals[0] / (intervals[1] || 1)
        multiplier = multiplier > 1 ? multiplier : 1
      }
    }

    const price = this.selectedVariant.price
    const compareAtPrice = this.selectedVariant.compare_at_price && this.selectedVariant.compare_at_price > price ? this.selectedVariant.compare_at_price : price

    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = price_adjustment.value
        discount.absolute = Math.round((price * price_adjustment.value) / 100.0)
        discount.amount = Math.round((price * price_adjustment.value) / 100.0)
        break
      case 'fixed_amount':
        discount.percent = Math.round(((price_adjustment.value * 1.0) / price) * 100.0)
        discount.absolute = price_adjustment.value
        discount.amount = price_adjustment.value
        break
      case 'price':
        discount.percent = Math.round((((compareAtPrice * multiplier - price_adjustment.value) * 1.0) / (compareAtPrice * multiplier)) * 100.0)
        discount.absolute = compareAtPrice * multiplier - price_adjustment.value
        discount.amount = price - price_adjustment.value
        break
    }

    return discount
  }

  money(cents, formatString = this.moneyFormat) {
    const placeholderRegex = /{{\s*(\w+)\s*}}/

    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) {
        return '0'
      }

      const numString = (number / 100.0).toFixed(precision)
      const parts = numString.split('.')
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands)
      const cents = parts[1] ? decimal + parts[1] : ''
      return dollars + cents
    }

    return formatString.replace(placeholderRegex, (match, placeholder) => {
      switch (placeholder) {
        case 'amount':
          return formatWithDelimiters(cents, 2)
        case 'amount_no_decimals':
          return formatWithDelimiters(cents, 0)
        case 'amount_with_comma_separator':
          return formatWithDelimiters(cents, 2, '.', ',')
        case 'amount_no_decimals_with_comma_separator':
          return formatWithDelimiters(cents, 0, '.', ',')
        case 'amount_with_apostrophe_separator':
          return formatWithDelimiters(cents, 2, "'", '.')
        case 'amount_no_decimals_with_space_separator':
          return formatWithDelimiters(cents, 0, ' ')
        case 'amount_with_space_separator':
          return formatWithDelimiters(cents, 2, ' ', ',')
        case 'amount_with_period_and_space_separator':
          return formatWithDelimiters(cents, 2, ' ', '.')
        default:
          return match
      }
    })
  }

  isSaleActive() {
    const now = new Date()
    return now >= this.saleStartDate && now <= this.saleEndDate
  }

  salePriceFromCents(cents) {
    const multiplier = 100 - this.saleDiscountPercent
    return Math.round(cents * multiplier / 100)
  }

  saleLabel() {
    return `4TH OF JULY SALE`
  }

  price(selling_plan, formatted = true) {
    if (this.rule) {
      return formatted
        ? this.money(this.selectedVariant.price - this.discount(selling_plan).amount - this.selectedVariant.price * (this.rule.discountAmount / 100))
        : this.selectedVariant.price - this.discount(selling_plan).amount - this.selectedVariant.price * (this.rule.discountAmount / 100)
    }

    return formatted ? this.money(this.selectedVariant.price - this.discount(selling_plan).amount) : this.selectedVariant.price - this.discount(selling_plan).amount
  }

  updateForm() {
    let $sellingPlan = this.querySelector('input[name=selling_plan]')

    if ($sellingPlan) {
      $sellingPlan.value = this.selectedSellingPlan ? this.selectedSellingPlan.id : ''
      $sellingPlan.dispatchEvent(new Event('change'))
    }
  }

  updateURLParams() {
    const url = new URL(window.location.href)

    if (this.selectedSellingPlan) {
      url.searchParams.set('selling_plan', this.selectedSellingPlan.id)
      window.history.replaceState({}, '', url.href)
    } else {
      url.searchParams.delete('selling_plan')
      window.history.replaceState({}, '', url.href)
    }
  }

  fetchProduct = handle => {
    return fetch(`/products/${handle}.js`)
      .then(response => response.json())
      .then(product => {
        this.product = product
        this.selectedVariant = product.variants[0]
        return product
      })
  }
}

if (!customElements.get('skio-plan-picker-v2')) {
  customElements.define('skio-plan-picker-v2', SkioPlanPickerV2)
}

if (window) window.SkioPlanPickerV2 = SkioPlanPickerV2
